import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    // Set persistence to LOCAL
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        // Persistence enabled silently
      })
      .catch((error) => {
        console.error('Error setting persistence:', error);
      });

    const unsubscribe = onAuthStateChanged(auth, 
      async (user) => {
        if (user) {
          try {
            // Check Firestore first for user data and verification status
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              
              // Get user's creation timestamp
              const creationTime = new Date(user.metadata.creationTime);
              const verificationCutoffDate = new Date('2024-02-10');
              
              // Check if user should bypass email verification
              const shouldBypassVerification = 
                creationTime < verificationCutoffDate || 
                userData.emailVerified === true;

              // Verification check (logged in development only)
              if (process.env.NODE_ENV === 'development') {
                console.log('Auth Context - Verification check:', {
                  email: user.email,
                  creationTime,
                  cutoffDate: verificationCutoffDate,
                  firestoreVerified: userData.emailVerified,
                  shouldBypass: shouldBypassVerification
                });
              }

              if (!user.emailVerified && !shouldBypassVerification) {
                if (process.env.NODE_ENV === 'development') {
                  console.log('Auth Context - User needs verification:', user.email);
                }
                await auth.signOut();
                setCurrentUser(null);
                setLoading(false);
                return;
              }

              // Update emailVerified in Firestore if needed
              if (!userData.emailVerified && (user.emailVerified || shouldBypassVerification)) {
                try {
                  await setDoc(userDocRef, {
                    emailVerified: true,
                    lastLogin: new Date()
                  }, { merge: true });
                  console.log('Updated emailVerified status in Firestore');
                } catch (updateError) {
                  console.error('Error updating Firestore:', updateError);
                }
              }
              
              setCurrentUser({ ...user, ...userData });
            } else {
              // If user document doesn't exist, create it
              try {
                const newUserData = {
                  email: user.email,
                  emailVerified: user.emailVerified,
                  createdAt: new Date(user.metadata.creationTime),
                  lastLogin: new Date()
                };
                await setDoc(userDocRef, newUserData);
                setCurrentUser({ ...user, ...newUserData });
              } catch (createError) {
                console.error('Error creating user document:', createError);
                setCurrentUser(user);
              }
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            // If Firestore access fails, check if we should still allow the user
            if (user.emailVerified) {
              setCurrentUser(user);
            } else {
              await auth.signOut();
              setCurrentUser(null);
            }
          }
        } else {
          setCurrentUser(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Auth state error:', error);
        setError(error);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  // Sign up function
  async function signup(email, password, displayName) {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email,
        displayName,
        createdAt: new Date(),
        lastLogin: new Date()
      });

      // Send email verification
      await sendEmailVerification(userCredential.user);
      
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign in function
  async function login(email, password) {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get existing user data
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      const existingData = userDoc.exists() ? userDoc.data() : {};
      
      // Update user document with merged data
      await setDoc(userDocRef, {
        ...existingData,
        email: email,
        lastLogin: new Date()
      }, { merge: true });

      return userCredential;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign out function
  async function logout() {
    try {
      setError(null);
      await signOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Password reset function
  async function resetPassword(email) {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Get user profile
  async function getUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Update user profile
  async function updateUserProfile(data) {
    try {
      setError(null);
      if (!currentUser) throw new Error('No user logged in');
      await setDoc(doc(db, 'users', currentUser.uid), data, { merge: true });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  const value = {
    currentUser,
    loading,
    error,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    getUserProfile,
    sendEmailVerification: (user) => sendEmailVerification(user)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <div>Loading authentication...</div>}
    </AuthContext.Provider>
  );
} 